"use client";

import { Server, Settings, Activity, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

interface Step {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface HowItWorksProps {
  config: {
    headline?: string;
    subheadline?: string;
    steps?: Step[];
    ctaText?: string;
    ctaUrl?: string;
  };
  locale: string;
}

const iconMap = {
  server: Server,
  settings: Settings,
  activity: Activity,
};

const defaultSteps: Step[] = [
  {
    number: "1",
    title: "Connect Your Servers",
    description: "Add PostgreSQL servers via SSH or agent. Automatic discovery detects databases, configurations, and existing backup tools.",
    icon: "server",
  },
  {
    number: "2",
    title: "Configure Backup Policies",
    description: "Set schedules, retention rules, and recovery objectives. Apply policies across multiple servers with one click.",
    icon: "settings",
  },
  {
    number: "3",
    title: "Monitor & Restore",
    description: "Real-time dashboards show backup status, storage trends, and alerts. One-click restore with point-in-time recovery.",
    icon: "activity",
  },
];

export function HowItWorksSection({ config, locale }: HowItWorksProps) {
  const {
    headline = "Get Started in Minutes",
    subheadline = "Three simple steps to enterprise-grade PostgreSQL backup management",
    steps = defaultSteps,
    ctaText = "Start Free Trial",
    ctaUrl = "/signup",
  } = config;
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-blue-500/5 to-background" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-green-400 bg-clip-text text-transparent">
            {headline}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            {subheadline}
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-12 sm:mb-16">
          {steps.map((step, index) => {
            const Icon = iconMap[step.icon as keyof typeof iconMap] || Server;
            return (
              <div key={index} className="relative group">
                {/* Connector line (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-blue-500/50 to-purple-500/50" />
                )}

                {/* Card */}
                <div className="relative h-full p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-xl hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
                  {/* Step number */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="mb-6 w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-blue-400" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold mb-3 text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link href={ctaUrl}>
            <Button
              size="lg"
              className="h-14 px-10 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              {ctaText}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
